<template>
  <Tooltip
    v-bind="attrs"
    :title="calcTitle"
    :overlay-class-name="componentsProps.overlayClassName"
    :overlay-style="tooltipOverlayStyle"
    :mouse-enter-delay="componentsProps.mouseEnterDelay"
    :get-popup-container="props.getPopupContainer"
    :arrow="componentsProps.arrow"
    @mouseenter.stop="mouseEnter"
  >
    <template v-for="(_, name) in $slots" :key="name" #[name]="slotData">
      <slot :name="name" v-bind="slotData || {}" />
    </template>
  </Tooltip>
</template>

<script setup lang="ts">
import type { CSSProperties } from 'vue'

import { isMobile } from '@peerup/utils'
import { Tooltip } from 'ant-design-vue'
import { computed, ref, useAttrs, watch } from 'vue'

import type { ZDTooltipProps } from './types'

export type { ZDTooltipProps }

const props = defineProps<ZDTooltipProps>()
const attrs = useAttrs()

/** 显式绑定：defineProps 已声明的 Tooltip 字段不会进入 attrs，需手动传给内部 Tooltip */
const tooltipOverlayStyle = computed(
  () => props.overlayStyle as CSSProperties | undefined,
)
// const slotContent = ref(null)
const calcTitle = ref()
const componentsProps = computed(() => {
  return {
    arrow: !props.hiddenArrow,
    overlayClassName: `${props?.overlayClassName || ''} zd-tooltip`,
    mouseEnterDelay: props.mouseEnterDelay || 0.1,
  }
})

// 试运行一段时间
const isContentOverflowing = (element: HTMLElement): boolean => {
  // 获取元素的滚动宽度和滚动高度
  const scrollWidth = element.scrollWidth
  const scrollHeight = element.scrollHeight

  // 获取元素的可视宽度和可视高度（不包括滚动条）
  const clientWidth = element.clientWidth
  const clientHeight = element.clientHeight

  // 如果设置了单行显示，则只判断宽度是否溢出
  if (props.singleLine) return scrollWidth > clientWidth

  // 如果滚动宽度大于可视宽度，或者滚动高度大于可视高度，则说明内容溢出
  return scrollWidth > clientWidth || scrollHeight > clientHeight
}
const mouseEnter = (e: MouseEvent) => {
  const { wrapClassName, title, alwaysShow } = props

  if (isMobile) {
    calcTitle.value = ''
    return
  }

  if (alwaysShow) {
    calcTitle.value = title
    return
  }
  const tipDom = e.target as HTMLElement
  const textDom = wrapClassName ? tipDom.querySelector(`.${wrapClassName}`) : tipDom
  const { scrollWidth = 0, clientWidth = 0 } = textDom || {}

  const isOverflowing = scrollWidth > clientWidth

  if (isOverflowing || props.disabled || isContentOverflowing(tipDom)) calcTitle.value = title
  else calcTitle.value = ''
}

watch(
  () => props.alwaysShow,
  newVal => {
    if (!newVal) {
      calcTitle.value = ''
    }
  },
  {
    immediate: true,
  },
)
</script>

<style lang="less" scoped></style>
